import { useEffect, useMemo, useState, type ReactNode } from 'react';
import Sidebar from '../components/dashboard/Sidebar';
import Topbar from '../components/dashboard/Topbar';
import { C2Button } from '../components/c2';
import { api, getToken, type Member, type User } from '../lib/api';
import { colorForString, initialsFromName, parseTimestamp, relativeTime } from '../lib/dashboardHelpers';
import type { PastelColor } from '../components/c2/Pill';

const pastelBg: Record<PastelColor, string> = {
  peach: 'var(--color-peach)',
  mint: 'var(--color-mint)',
  sky: 'var(--color-sky)',
  lemon: 'var(--color-lemon)',
  lilac: 'var(--color-lilac)',
};
const pastelDeep: Record<PastelColor, string> = {
  peach: 'var(--color-peach-deep)',
  mint: 'var(--color-mint-deep)',
  sky: 'var(--color-sky-deep)',
  lemon: 'var(--color-lemon-deep)',
  lilac: 'var(--color-lilac-deep)',
};

interface MiniStatProps {
  label: string;
  value: number | string;
  warn?: boolean;
}

function MiniStat({ label, value, warn }: MiniStatProps) {
  return (
    <div
      className="bg-surface flex items-baseline justify-between"
      style={{
        border: '1px solid var(--color-hairline)',
        borderRadius: 14,
        padding: '14px 18px',
        gap: 12,
      }}
    >
      <div className="text-[13px] text-ink-soft font-medium">{label}</div>
      <div
        className="font-display font-extrabold leading-none"
        style={{
          fontSize: 24,
          color: warn ? 'var(--color-peach-deep)' : 'var(--color-ink)',
          letterSpacing: '-0.02em',
        }}
      >
        {value}
      </div>
    </div>
  );
}

interface InfoBannerProps {
  domain: string;
}

function DomainAutoJoinBanner({ domain }: InfoBannerProps) {
  return (
    <div
      className="mb-2"
      style={{
        background: 'var(--accent-tint)',
        border: '1px solid var(--accent-soft)',
        borderRadius: 14,
        padding: '14px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <div
        className="w-7 h-7 rounded-md flex items-center justify-center text-base shrink-0"
        style={{ background: 'var(--accent-soft)', color: 'var(--accent-deep)' }}
      >
        ✦
      </div>
      <div className="flex-1">
        <div
          className="font-display font-bold leading-tight"
          style={{ fontSize: 14, color: 'var(--accent-deep)', letterSpacing: '-0.01em' }}
        >
          Anyone with an{' '}
          <span className="font-mono" style={{ background: 'var(--accent-soft)', padding: '1px 6px', borderRadius: 4, fontSize: 12 }}>
            @{domain}
          </span>{' '}
          email joins automatically
        </div>
        <div className="text-xs text-ink-soft mt-0.5">
          No manual invites needed. Members appear here as they sign up.
        </div>
      </div>
    </div>
  );
}

interface RowProps {
  member: Member;
  isYou: boolean;
  isLast: boolean;
}

function MemberRow({ member, isYou, isLast }: RowProps) {
  const color = colorForString(member.email);
  const lastActive = member.extensionLastSeen ? relativeTime(member.extensionLastSeen) : 'Never';
  const isDormant = !member.extensionLastSeen ||
    (Date.now() - parseTimestamp(member.extensionLastSeen)) > 60 * 86400000;

  return (
    <div
      className="grid items-center transition-colors duration-150"
      style={{
        gridTemplateColumns: 'minmax(240px, 2fr) 100px 120px',
            gap: 16,
        alignItems: 'center',
        padding: '14px 20px',
        borderBottom: isLast ? 'none' : '1px solid var(--color-hairline)',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--color-bg-alt)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs shrink-0"
          style={{ background: pastelBg[color], color: pastelDeep[color] }}
        >
          {initialsFromName(member.name)}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-sm font-semibold text-ink">
            <span className="overflow-hidden text-ellipsis whitespace-nowrap min-w-0">
              {member.name}
            </span>
            {isYou && (
              <span
                className="font-bold uppercase"
                style={{
                  fontSize: 10,
                  letterSpacing: '0.05em',
                  background: 'var(--accent-tint)',
                  color: 'var(--accent-deep)',
                  padding: '1px 6px',
                  borderRadius: 4,
                }}
              >
                You
              </span>
            )}
            {isDormant && !isYou && (
              <span
                className="font-bold uppercase"
                style={{
                  fontSize: 10,
                  letterSpacing: '0.05em',
                  background: 'var(--color-bg-alt)',
                  color: 'var(--color-ink-muted)',
                  padding: '1px 6px',
                  borderRadius: 4,
                }}
              >
                Dormant
              </span>
            )}
          </div>
          <div className="text-xs text-ink-muted overflow-hidden text-ellipsis whitespace-nowrap">
            {member.email}
          </div>
        </div>
      </div>

      <div
        className="text-right font-mono font-semibold text-[13px]"
        style={{ color: member.linkCount === 0 ? 'var(--color-ink-muted)' : 'var(--color-ink)' }}
      >
        {member.linkCount}
      </div>

      <div className="text-xs" style={{ color: isDormant ? 'var(--color-peach-deep)' : 'var(--color-ink-soft)' }}>
        {lastActive}
      </div>
    </div>
  );
}

// Members page rendered for users on a personal workspace. There's no team to
// manage, so the page becomes a self-portrait + a pitch for the team workflow.
function PersonalMembersView({ user }: { user: User | null }) {
  if (!user) return null;
  const color = colorForString(user.email);
  const initials = initialsFromName(user.name);
  return (
    <>
      <div className="mb-6">
        <h1
          className="font-display font-extrabold text-ink m-0"
          style={{ fontSize: 32, letterSpacing: '-0.02em' }}
        >
          Members
        </h1>
        <p className="text-sm text-ink-soft mt-2 m-0 leading-[1.55]" style={{ maxWidth: 640 }}>
          You're on a personal workspace, so it's just you here. When you sign up with a
          work email, this page lists everyone on your team along with their link counts and
          last active time.
        </p>
      </div>

      <div
        className="bg-surface overflow-hidden mb-10"
        style={{ border: '1px solid var(--color-hairline)', borderRadius: 14 }}
      >
        <div
          className="grid items-center bg-bg-alt"
          style={{
            gridTemplateColumns: 'minmax(240px, 2fr) 100px 120px',
            gap: 16,
            padding: '10px 20px',
            borderBottom: '1px solid var(--color-hairline)',
            fontSize: 11,
            fontWeight: 700,
            color: 'var(--color-ink-muted)',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          }}
        >
          <div>Teammate</div>
          <div className="text-right">Links</div>
          <div>Last active</div>
        </div>
        <div
          className="grid items-center"
          style={{
            gridTemplateColumns: 'minmax(240px, 2fr) 100px 120px',
            gap: 16,
            alignItems: 'center',
            padding: '14px 20px',
          }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs shrink-0"
              style={{ background: pastelBg[color], color: pastelDeep[color] }}
            >
              {initials}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 text-sm font-semibold text-ink">
                <span className="overflow-hidden text-ellipsis whitespace-nowrap min-w-0">
                  {user.name}
                </span>
                <span
                  className="font-bold uppercase"
                  style={{
                    fontSize: 10,
                    letterSpacing: '0.05em',
                    background: 'var(--accent-tint)',
                    color: 'var(--accent-deep)',
                    padding: '1px 6px',
                    borderRadius: 4,
                  }}
                >
                  You
                </span>
              </div>
              <div className="text-xs text-ink-muted overflow-hidden text-ellipsis whitespace-nowrap">
                {user.email}
              </div>
            </div>
          </div>
          <div className="text-right font-mono font-semibold text-[13px] text-ink-muted">—</div>
          <div className="text-xs text-ink-soft">Now</div>
        </div>
      </div>

      <div className="mb-3">
        <div
          className="font-bold uppercase mb-1.5"
          style={{ fontSize: 11, color: 'var(--color-ink-muted)', letterSpacing: '0.08em' }}
        >
          Bring your team
        </div>
        <h2
          className="font-display font-bold text-ink m-0"
          style={{ fontSize: 24, letterSpacing: '-0.02em' }}
        >
          Tidy Go Links is better with the people you work with.
        </h2>
        <p className="text-sm text-ink-soft mt-2 m-0 leading-[1.55]" style={{ maxWidth: 640 }}>
          Sign up again with your work email and your whole team gets a shared go/ namespace.
          Onboarding is the same — under a minute.
        </p>
      </div>

      <div className="grid mb-6" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
        <BenefitCard
          title="One source of truth"
          body="Everyone reaches the same go/handbook, go/oncall, go/roadmap. No more pasting URLs into Slack or hunting through bookmarks."
          icon={
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
          }
        />
        <BenefitCard
          title="Auto-join by domain"
          body="Anyone with a matching work email joins automatically when they sign up. No invites, no seat licensing, no admin overhead."
          icon={
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          }
        />
        <BenefitCard
          title="Free for unlimited users"
          body="Most go/ link tools charge per seat. Tidy Go Links doesn't — bring everyone, no usage caps, no surprise bills at the end of the quarter."
          icon={
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="1" x2="12" y2="23" />
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
          }
        />
      </div>

      <div
        className="flex items-center justify-between flex-wrap gap-4"
        style={{
          background: 'var(--accent-tint)',
          border: '1px solid var(--accent-soft)',
          borderRadius: 14,
          padding: '18px 22px',
        }}
      >
        <div>
          <div
            className="font-display font-bold leading-tight"
            style={{ fontSize: 16, color: 'var(--accent-deep)', letterSpacing: '-0.01em' }}
          >
            Ready to roll this out at work?
          </div>
          <div className="text-xs text-ink-soft mt-1">
            Sign up with your work email and your team auto-joins as they install the extension.
          </div>
        </div>
        <C2Button variant="accent" size="md" onClick={() => { window.location.href = '/signup'; }}>
          Create a team workspace →
        </C2Button>
      </div>
    </>
  );
}

function BenefitCard({ title, body, icon }: { title: string; body: string; icon: ReactNode }) {
  return (
    <div
      className="bg-surface"
      style={{
        border: '1px solid var(--color-hairline)',
        borderRadius: 14,
        padding: '18px 20px',
      }}
    >
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center mb-3"
        style={{ background: 'var(--accent-soft)', color: 'var(--accent-deep)' }}
      >
        {icon}
      </div>
      <div
        className="font-display font-bold text-ink mb-1.5"
        style={{ fontSize: 15, letterSpacing: '-0.01em' }}
      >
        {title}
      </div>
      <div className="text-[13px] text-ink-soft leading-[1.5]">{body}</div>
    </div>
  );
}

export default function MembersPage() {
  const [user, setUser] = useState<User | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!getToken()) {
      window.location.href = '/login';
    }
  }, []);

  const refresh = async () => {
    try {
      const [me, org] = await Promise.all([api.me(), api.organization()]);
      setUser(me);
      setMembers(org.members);
      setLoadError(null);
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 401 || status === 403) {
        window.location.href = '/login';
        return;
      }
      setLoadError((err as Error).message || 'Could not load members');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const isPersonal = !!user && (
    !!user.workspace?.is_personal
    || ['gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com', 'icloud.com', 'proton.me', 'protonmail.com']
        .includes((user.email.split('@')[1] ?? '').toLowerCase())
  );

  const filtered = useMemo(() => {
    let out = members;
    if (search) {
      const q = search.toLowerCase();
      out = out.filter(m => m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q));
    }
    return out;
  }, [members, search]);

  const dormantCount = members.filter(m => {
    if (!m.extensionLastSeen) return true;
    return (Date.now() - parseTimestamp(m.extensionLastSeen)) > 60 * 86400000;
  }).length;

  const totalLinks = members.reduce((acc, m) => acc + m.linkCount, 0);
  const domain = user?.email?.split('@')[1] ?? 'company.com';

  return (
    <div className="flex min-h-screen bg-bg font-body">
      <Sidebar user={user} />

      <div className="flex-1 min-w-0 flex flex-col">
        <Topbar
          crumbs={[user?.email?.split('@')[1]?.split('.')[0] ?? 'Workspace', 'Members']}
        />

        <div style={{ padding: '28px 32px 80px', maxWidth: 1280, width: '100%', margin: '0 auto' }} className="w-full">
          {loading ? (
            <div className="text-center text-ink-soft" style={{ padding: '60px 20px' }}>Loading…</div>
          ) : loadError ? (
            <div className="text-center text-ink-soft" style={{ padding: '60px 20px' }}>{loadError}</div>
          ) : isPersonal ? (
            <PersonalMembersView user={user} />
          ) : (
            <>
              <div className="mb-6">
                <h1
                  className="font-display font-extrabold text-ink m-0"
                  style={{ fontSize: 32, letterSpacing: '-0.02em' }}
                >
                  Members
                </h1>
                <p className="text-sm text-ink-soft mt-2 m-0 leading-[1.55]" style={{ maxWidth: 640 }}>
                  Everyone with an @{domain} email joins automatically. All teammates have
                  the same access — view every link, edit your own.
                </p>
              </div>

              <div className="grid mb-6" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
                <MiniStat label="Total teammates" value={members.length} />
                <MiniStat label="Total links" value={totalLinks} />
                <MiniStat label="Dormant (60d+)" value={dormantCount} warn={dormantCount > 0} />
              </div>

              <DomainAutoJoinBanner domain={domain} />

              <div className="flex items-center" style={{ gap: 10, padding: '16px 0 12px' }}>
                <div
                  className="flex items-center bg-surface flex-1"
                  style={{
                    gap: 8,
                    maxWidth: 340,
                    border: '1px solid var(--color-hairline)',
                    borderRadius: 14,
                    padding: '7px 12px',
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-ink-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                  <input
                    placeholder="Search by name or email…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="flex-1 border-0 bg-transparent outline-none text-[13px] text-ink min-w-0"
                  />
                </div>
                <div className="flex-1" />
                <div className="text-xs text-ink-muted">
                  {filtered.length} of {members.length}
                </div>
              </div>

              <div
                className="bg-surface overflow-hidden"
                style={{ border: '1px solid var(--color-hairline)', borderRadius: 14 }}
              >
                <div
                  className="grid items-center bg-bg-alt"
                  style={{
                    gridTemplateColumns: 'minmax(240px, 2fr) 100px 120px',
            gap: 16,
                    padding: '10px 20px',
                    borderBottom: '1px solid var(--color-hairline)',
                    fontSize: 11,
                    fontWeight: 700,
                    color: 'var(--color-ink-muted)',
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                  }}
                >
                  <div>Teammate</div>
                  <div className="text-right">Links</div>
                  <div>Last active</div>
                </div>

                {filtered.length === 0 ? (
                  <div className="text-center text-ink-muted text-sm" style={{ padding: '48px 20px' }}>
                    No teammates match that search.
                  </div>
                ) : (
                  filtered.map((m, i) => (
                    <MemberRow
                      key={m.email}
                      member={m}
                      isYou={user ? m.id === user.id : false}
                      isLast={i === filtered.length - 1}
                    />
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
